/**
 * Hand a prefill payload to another page in ANOTHER TAB.
 *
 * Used for the Make & Model -> Online Policy handoff and for Premium
 * Calculation -> Motor Calculation.
 *
 * The in-app route already carries this through React Router's location.state
 * (see MakeModel.js -> CreatePolicyFromRow). That state lives in the history
 * entry of one browsing context, so it does not survive window.open — a new tab
 * starts with a history of its own and would arrive with nothing.
 *
 * localStorage is the only channel both tabs can see. The payload is written
 * under a one-shot token, the token travels in the URL, and the reader deletes
 * the entry as it consumes it: a prefill is used exactly once, and a refresh
 * afterwards opens a blank form rather than silently re-filling it.
 */

const PREFIX = "onlinePolicyPrefill:";

/** How long a handoff stays usable. Long enough to survive a slow tab open,
 *  short enough that a token pasted into a URL tomorrow does nothing. */
const TTL_MS = 5 * 60 * 1000;

/** Drop anything past its TTL, so an abandoned handoff cannot accumulate. */
const purgeExpired = () => {
  try {
    const now = Date.now();
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(PREFIX)) continue;
      try {
        const { savedAt } = JSON.parse(localStorage.getItem(key)) || {};
        if (!savedAt || now - savedAt > TTL_MS) localStorage.removeItem(key);
      } catch (parseError) {
        localStorage.removeItem(key); // unreadable — no reason to keep it
      }
    }
  } catch (storageError) {
    // Storage disabled entirely. Nothing to purge, and nothing to report.
  }
};

/**
 * Store a prefill payload and return the token that retrieves it.
 * @returns {string|null} null when storage is unavailable — the caller should
 *   then tell the user rather than opening a tab that cannot prefill.
 */
export const stashPrefill = (payload) => {
  if (!payload) return null;
  purgeExpired();

  const token = `${payload._id || "makemodel"}-${Date.now()}`;
  try {
    localStorage.setItem(
      `${PREFIX}${token}`,
      JSON.stringify({ savedAt: Date.now(), prefill: payload })
    );
    return token;
  } catch (storageError) {
    console.error("Could not stash the policy prefill:", storageError);
    return null;
  }
};

/**
 * Retrieve and CONSUME a stashed prefill.
 * @returns {object|null} the payload, or null if absent or expired.
 */
export const readStashedPrefill = (token) => {
  if (!token) return null;
  const key = `${PREFIX}${token}`;

  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    localStorage.removeItem(key); // one shot, whatever happens next

    const { savedAt, prefill } = JSON.parse(raw) || {};
    if (!savedAt || Date.now() - savedAt > TTL_MS) return null;
    return prefill || null;
  } catch (storageError) {
    console.error("Could not read the policy prefill:", storageError);
    return null;
  }
};

/**
 * The URL that opens `path` with this token's prefill.
 *
 * Absolute rather than relative: this is fed to window.open, and a relative
 * path there would resolve against whatever nested route the opener happens to
 * be on.
 */
export const prefillUrl = (token, path = "/onlinePolicy") =>
  `${window.location.origin}${path}?prefill=${encodeURIComponent(token)}`;

/**
 * Motor Calculation only: the CC to price THIRD PARTY on, when the policy that
 * opened the page is sold by two insurers with a vehicle each.
 *
 * That page has ONE CC box, and the policy fills it with its OD vehicle's CC.
 * The TP vehicle's CC rides beside the form values as `prefill.tpCc`, and it
 * applies only while the CC box still holds what the policy sent: once someone
 * types another CC the page is theirs again and one CC prices both, as it
 * always has.
 *
 * @returns {number|null} the TP vehicle's CC, or null for "no override".
 */
export const tpCcOverride = (prefill, currentCc) => {
  const tpCc = Number(prefill?.tpCc);
  if (!Number.isFinite(tpCc) || tpCc <= 0) return null;
  const sent = Number(prefill?.inputData?.cC);
  return Number.isFinite(sent) && Number(currentCc) === sent ? tpCc : null;
};
